// PublicProfilePublicPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./style.css";

const PublicProfilePublicPage = () => {
    const { targetUserId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [profile, setProfile] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState("");
    const [videoUrls, setVideoUrls] = useState({});
    const [imageUrls, setImageUrls] = useState({});

    // Replace with your actual Cloud Run endpoint
    const baseUrl =
        process.env.REACT_APP_BACKEND_URL ||
        "https://dns.seebreakthrough.com";

    // A function to open the app if installed, otherwise fall back to the App Store or download page.
    const openInApp = () => {
        // Create your custom URI scheme or universal link.
        // e.g. "seebreakthrough://public-profile/username"
        // Using the targetUserId from the URL param:
        const deepLink = `seebreakthrough://public-profile/${encodeURIComponent(targetUserId)}`;

        // Start a timer to fall back to the app download page if the app isn't installed
        const fallbackTimer = setTimeout(() => {
            // If the app is not installed, this is triggered ~1.5 seconds later
            window.location.href = "https://seebreakthrough.com/#download";
        }, 1500);

        // Try to open the app via the custom URL scheme
        window.location.href = deepLink;

        // If the app opens successfully, most browsers will “pause” or navigate away immediately,
        // canceling this fallback. If it fails, fallback triggers after ~1.5s.
    };

    useEffect(() => {
        if (!targetUserId) return;

        const fetchProfile = async () => {
            setIsLoading(true);
            setErrorMsg("");

            try {
                const url = `${baseUrl}/PublicProfileWeb?targetUserId=${encodeURIComponent(
                    targetUserId
                )}`;
                const resp = await fetch(url);
                if (!resp.ok) {
                    throw new Error(`HTTP error: ${resp.status}`);
                }
                const data = await resp.json();
                if (data.error) {
                    throw new Error(data.error);
                }

                setProfile(data.profile);

                // If there's a profile image path, fetch its signed URL:
                if (data.profile?.userProfileImage) {
                    await fetchProfileImageUrl(data.profile.userProfileImage);
                } else {
                    setProfileImageUrl("");
                }
            } catch (error) {
                console.error("Error fetching public profile:", error);
                setErrorMsg(error.message || "Error loading profile");
            } finally {
                setIsLoading(false);
            }
        };

        const fetchProfileImageUrl = async (objectPath) => {
            try {
                const resp = await fetch(
                    `${baseUrl}/getImageSignedUrl?objectPath=${encodeURIComponent(objectPath)}`
                );
                if (!resp.ok) {
                    throw new Error(`HTTP error: ${resp.status}`);
                }
                const data = await resp.json();
                setProfileImageUrl(data.signedUrl);
            } catch (err) {
                console.error("Error fetching profile image signed URL:", err);
            }
        };

        fetchProfile();
    }, [targetUserId, baseUrl]);

    // Fetch signed video URLs for each post (if any)
    useEffect(() => {
        if (!profile || !profile.posts) return;

        const fetchSignedVideos = async () => {
            const newVideoUrls = { ...videoUrls };

            for (const post of profile.posts) {
                const videoPath = post.videoURL;
                if (videoPath) {
                    try {
                        const signedResp = await fetch(
                            `${baseUrl}/getVideoSignedUrl?videoPath=${encodeURIComponent(videoPath)}`
                        );
                        if (signedResp.ok) {
                            const { signedUrl } = await signedResp.json();
                            newVideoUrls[videoPath] = signedUrl;
                        }
                    } catch (err) {
                        console.error("Error fetching signed video URL:", err);
                    }
                }
            }
            setVideoUrls(newVideoUrls);
        };

        fetchSignedVideos();
        // NOTE: Omitting videoUrls from dependency array to avoid re-trigger loops
    }, [profile, baseUrl]);

    // Fetch signed image URLs for each post (if any)
    useEffect(() => {
        if (!profile || !profile.posts) return;

        const fetchSignedImages = async () => {
            const newImageUrls = { ...imageUrls };

            for (const post of profile.posts) {
                const imagePath = post.photoURL;
                if (imagePath) {
                    // Remove bucket prefix (e.g. bucketName/) and ensure activityImages/ prefix
                    const pathParts = imagePath.split("/");
                    const pathWithoutBucket = pathParts.length > 1
                        ? pathParts.slice(1).join("/")
                        : imagePath;
                    const objectPath = pathWithoutBucket.startsWith("activityImages/")
                        ? pathWithoutBucket
                        : `activityImages/${pathWithoutBucket}`;
                    try {
                        const resp = await fetch(
                            `${baseUrl}/getImageSignedUrl?objectPath=${encodeURIComponent(objectPath)}`
                        );
                        if (resp.ok) {
                            const { signedUrl } = await resp.json();
                            newImageUrls[imagePath] = signedUrl; // map original path to URL
                        }
                    } catch (err) {
                        console.error("Error fetching signed image URL:", err);
                    }
                }
            }
            setImageUrls(newImageUrls);
        };

        fetchSignedImages();
    }, [profile, baseUrl]);

    // Set the page title dynamically
    useEffect(() => {
        if (targetUserId) {
            document.title = `${targetUserId}'s Breakthrough`;
        }
    }, [targetUserId]);

    // --- Loading/Error states ---
    if (isLoading) {
        return <div className="public-profile-loading">Loading profile...</div>;
    }
    if (errorMsg) {
        return <div className="public-profile-error">{errorMsg}</div>;
    }
    if (!profile) {
        return <div className="public-profile-error">No profile found.</div>;
    }

    const { username, bio, points, followers = [], following = [], posts = [] } = profile;

    // Just an example engagement callback
    const handleEngagementClick = () => {
        window.open("https://seebreakthrough.com/#download", "_blank");
    };

    return (
        <section className="public-profile-section">
            {/* ---------- 1) Top Banner with "Open in App" button ---------- */}
            <div className="public-profile-banner">
                <p>
                    Want the full experience?{" "}
                    <button onClick={openInApp} style={{marginLeft: "1rem"}}>
                        Open in App
                    </button>
                </p>
            </div>

            {/* ---------- 2) User Info (Header) ---------- */}
            <div className="public-profile-header">
                <div className="profile-image-container">
                    {profileImageUrl ? (
                        <img
                            src={profileImageUrl}
                            alt={`${username}'s profile`}
                            className="profile-image"
                        />
                    ) : (
                        <div className="profile-image placeholder">No Image</div>
                    )}
                </div>

                <h2 className="profile-username">{username}'s Profile</h2>

                <div className="profile-bio">
                    {bio || "This user hasn't written a bio yet."}
                </div>
                <div className="profile-points">Points: {points ?? 0}</div>

                <div className="profile-follow-stats">
                    <div>
                        <span className="follow-count">{following.length}</span>
                        <span className="follow-label">Following</span>
                    </div>
                    <div>
                        <span className="follow-count">{followers.length}</span>
                        <span className="follow-label">Followers</span>
                    </div>
                </div>
            </div>

            {/* ---------- 3) Public Posts + Videos ---------- */}
            <div className="public-profile-posts">
                <h3>Public Posts</h3>
                {posts.length === 0 ? (
                    <p className="no-posts">No public posts yet.</p>
                ) : (
                    <div className="posts-grid">
                        {posts.map((post, idx) => (
                            <div key={idx} className="post-card">
                                {/* Video */}
                                {post.videoURL && videoUrls[post.videoURL] && (
                                    <video
                                        src={videoUrls[post.videoURL]}
                                        controls
                                        style={{ width: "100%", marginTop: "0.5rem" }}
                                    />
                                )}
                                {/* Photo */}
                                {post.photoURL && imageUrls[post.photoURL] && (
                                    <img
                                        src={imageUrls[post.photoURL]}
                                        alt={post.caption || "Post image"}
                                        style={{ width: "100%", marginTop: "0.5rem" }}
                                    />
                                )}
                                {/* Journal */}
                                {post.journalText && (
                                    <p className="post-journal">{post.journalText}</p>
                                )}
                                {/* Post caption, status, views */}
                                <p className="post-caption">{post.caption}</p>
                                {post.status && <p className="post-status">Status: {post.status}</p>}
                                {typeof post.viewCount === "number" && (
                                    <p className="post-views">Views: {post.viewCount}</p>
                                )}

                                {/* Likes and Comments */}
                                <div className="post-engagement">
                                    <div className="like-container" onClick={handleEngagementClick}>
                                        <i className="fa fa-heart" aria-hidden="true"></i>
                                        <span className="like-count">{post.likes?.length || 0} Likes</span>
                                    </div>
                                    <div className="comment-container" onClick={handleEngagementClick}>
                                        <i className="fa fa-comment" aria-hidden="true"></i>
                                        <span className="comment-count">
                      {post.comments?.length || 0} Comments
                    </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default PublicProfilePublicPage;